/**
 * Scroll reveal — ported from SeventyTwo 2026 Website.
 * Line-splits headings; fades/rises .reveal and .reveal-children > * on intersect.
 */

var originalHtml = new WeakMap();
var prefersMotion = window.matchMedia('(prefers-reduced-motion: no-preference)');

function splitHeadingLines(heading) {
    if (!originalHtml.has(heading)) {
        originalHtml.set(heading, heading.innerHTML);
        heading.setAttribute(
            'aria-label',
            heading.textContent.replace(/\s+/g, ' ').trim()
        );
    }

    heading.innerHTML = originalHtml.get(heading);

    // Wrap each word in a bare <span> so we can measure line breaks
    Array.from(heading.childNodes).forEach(function (node) {
        if (node.nodeType !== Node.TEXT_NODE) {
            return;
        }

        node.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) {
                return;
            }
            if (/^\s+$/.test(part)) {
                node.before(part);
                return;
            }
            var span = document.createElement('span');
            span.textContent = part;
            node.before(span);
        });
        node.remove();
    });

    var lines = [];
    var lastTop = -Infinity;

    Array.from(heading.childNodes).forEach(function (node) {
        if (node.nodeName === 'BR') {
            lastTop = Infinity;
            return;
        }

        // Only elements participate in line-break measurement; text nodes (spaces) still get kept
        if (node.nodeType === Node.ELEMENT_NODE) {
            var top = node.getBoundingClientRect().top;
            if (top > lastTop + 1 || lastTop === Infinity) {
                lines.push([]);
            }
            lastTop = top;
        }

        if (!lines.length) {
            lines.push([]);
        }

        // Unwrap bare word spans back to text; keep spaces and marked-up nodes
        var isBareSpan = node.nodeName === 'SPAN' && !node.attributes.length;
        lines[lines.length - 1].push(isBareSpan ? node.textContent : node);
    });

    heading.replaceChildren.apply(
        heading,
        lines.map(function (parts) {
            var line = document.createElement('span');
            line.className = 'reveal-line';
            line.setAttribute('aria-hidden', 'true');
            line.append.apply(line, parts);
            return line;
        })
    );
}

function shouldSkipHeading(heading) {
    return (
        heading.closest('.reveal, .reveal-children > *, .legal, .reveal-wrapper, .footer-reveal') ||
        !heading.getClientRects().length
    );
}

export function initReveal() {
    document.documentElement.classList.add('js');

    if (!prefersMotion.matches) {
        return;
    }

    var headings = Array.from(document.querySelectorAll('h1, h2, h3, h4'));
    var blocks = Array.from(
        document.querySelectorAll('.reveal, .reveal-children > *')
    );

    var observer = new IntersectionObserver(
        function (entries) {
            entries
                .filter(function (entry) {
                    return entry.isIntersecting;
                })
                .map(function (entry) {
                    return entry.target;
                })
                .sort(function (a, b) {
                    return a.compareDocumentPosition(b) &
                        Node.DOCUMENT_POSITION_FOLLOWING
                        ? -1
                        : 1;
                })
                .forEach(function (el, index) {
                    el.dataset.revealOrder = String(Math.min(index, 11));
                    el.dataset.reveal = 'shown';
                    observer.unobserve(el);
                });
        },
        { rootMargin: '0px 0px -10% 0px' }
    );

    var start = function () {
        headings.forEach(function (heading) {
            // Hero H1 paints immediately for LCP — skip line-split / hide cycle
            if (heading.closest('.page-hero')) {
                heading.dataset.reveal = 'shown';
                return;
            }
            if (shouldSkipHeading(heading)) {
                heading.dataset.reveal = 'shown';
                return;
            }
            splitHeadingLines(heading);
            heading.dataset.reveal = 'hidden';
            observer.observe(heading);
        });

        blocks.forEach(function (el) {
            // Skip footer-reveal panel (fixed; always "in view")
            if (el.closest('.reveal-wrapper, .footer-reveal')) {
                el.dataset.reveal = 'shown';
                return;
            }
            el.dataset.reveal = 'hidden';
            observer.observe(el);
        });
    };

    // Don't block first paint on slow webfonts (fonts.ready can stall LCP)
    var fontsReady =
        document.fonts && document.fonts.ready
            ? document.fonts.ready
            : Promise.resolve();
    Promise.race([
        fontsReady,
        new Promise(function (resolve) {
            setTimeout(resolve, 120);
        }),
    ]).then(start);

    // Re-measure heading lines when viewport width changes
    var lastWidth = window.innerWidth;
    var resizeTimer;
    window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            if (window.innerWidth === lastWidth) {
                return;
            }
            lastWidth = window.innerWidth;
            headings.forEach(function (heading) {
                if (originalHtml.has(heading)) {
                    splitHeadingLines(heading);
                }
            });
        }, 150);
    });
}
